import React from "react";
import { Link, Typography } from "@mui/material";
import LegalDocumentLayout, { LegalSection } from "./LegalDocumentLayout";

const contactEmail = "arjo@stanford.edu";

const sections: LegalSection[] = [
  {
    id: "scope",
    title: "Scope and relationship to study consent",
    content: (
      <>
        <Typography variant="body1" paragraph>
          This Privacy Policy describes how StudySync collects, uses, shares,
          and protects information when you use the StudySync web application,
          mobile application, researcher dashboard, participant enrollment flow,
          support tools, and related services that link to this Policy.
        </Typography>
        <Typography variant="body1" paragraph>
          StudySync supports research workflows. If you participate in a
          specific study, the study consent form, IRB-approved materials, and
          researcher instructions may describe additional or different data
          practices for that study. Those study-specific materials control for
          the study where they conflict with this Policy.
        </Typography>
        <Typography variant="body1" paragraph>
          This Policy is not a HIPAA Notice of Privacy Practices and does not
          replace any notice or patient-rights document provided by Stanford
          Health Care, another health care provider, or a covered entity.
        </Typography>
      </>
    ),
  },
  {
    id: "information-collected",
    title: "Information we collect",
    content: (
      <>
        <Typography variant="body1" paragraph>
          The information we collect depends on your role, your study, and the
          permissions you choose to grant.
        </Typography>
        <Typography component="ul">
          <li>
            <strong>Account and profile information:</strong> name, email
            address, institution, role, account status, authentication records,
            researcher application information, and account preferences.
          </li>
          <li>
            <strong>Participant and study information:</strong> study codes,
            participant identifiers, tracking IDs, enrollment status,
            verification status, contact information, study communications, and
            consent-related workflow records.
          </li>
          <li>
            <strong>Connected device and health information:</strong> data you
            authorize StudySync to receive from Apple Health/HealthKit, Fitbit,
            or other supported integrations. Depending on the study and your
            permissions, this may include activity, sleep, heart rate, heart
            rate variability, respiratory, body measurement, nutrition,
            mindfulness, mobility, vitals, workout, and clinical record related
            information.
          </li>
          <li>
            <strong>Research platform records:</strong> data extraction
            requests, export metadata, dashboard activity, adherence monitoring
            records, support requests, and records needed to maintain study
            access and auditability.
          </li>
          <li>
            <strong>Technical and usage information:</strong> IP address,
            browser type, operating system, device information, log data, crash
            and performance information, and analytics events used to operate,
            secure, and improve StudySync.
          </li>
        </Typography>
      </>
    ),
  },
  {
    id: "connected-services",
    title: "Account sign-in and connected service data",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync may receive information from third-party services only when
          the feature is configured for your organization, you sign in with that
          service, or you authorize a device or account connection.
        </Typography>
        <Typography component="ul">
          <li>
            <strong>Google Sign-In:</strong> if you choose to sign in with
            Google, StudySync receives basic account information such as your
            email address, name or display name, profile photo, account
            identifier, and authentication tokens needed to verify your session.
          </li>
          <li>
            <strong>Google Workspace and Google Cloud:</strong> when used for
            study administration, StudySync may process Google group names,
            group email addresses, member email addresses, membership roles, and
            access-control records so approved researchers and collaborators can
            access the correct study resources.
          </li>
          <li>
            <strong>Apple Health/HealthKit, Fitbit, and similar integrations:</strong>{" "}
            when you authorize a supported device or account connection,
            StudySync receives the health, fitness, and device data allowed by
            your permissions and the applicable study materials.
          </li>
          <li>
            <strong>Verification and communications services:</strong> if a
            study uses email, SMS, support, or security verification features,
            StudySync may process the contact details and delivery records
            needed to provide those features.
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          We do not ask connected services for more data than is needed for the
          feature, account, study, security, or support purpose being provided.
        </Typography>
      </>
    ),
  },
  {
    id: "use",
    title: "How we use information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We use information to operate StudySync and support approved research
          activities.
        </Typography>
        <Typography component="ul">
          <li>Authenticate users and maintain secure researcher accounts.</li>
          <li>Enroll participants and verify study participation.</li>
          <li>Connect authorized device accounts and collect permitted data.</li>
          <li>
            Manage study access, researcher collaboration, and data-export
            permissions.
          </li>
          <li>Generate research dashboards, exports, reports, and analytics.</li>
          <li>Monitor platform reliability, security, errors, and misuse.</li>
          <li>Provide support and send operational communications.</li>
          <li>Comply with applicable law, institutional requirements, study
            protocols, and legal process.</li>
        </Typography>
        <Typography variant="body1" paragraph>
          StudySync is designed for research data collection and analysis. It is
          not a medical device, emergency service, diagnosis tool, or substitute
          for care from a qualified health professional.
        </Typography>
      </>
    ),
  },
  {
    id: "healthkit",
    title: "Apple Health, HealthKit, Fitbit, and research data",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync collects connected device and health information only when
          you authorize the relevant integration or participate in a study that
          asks you to provide that data. You can decline or revoke permissions,
          though doing so may limit your ability to participate in a study or may
          prevent future data collection.
        </Typography>
        <Typography component="ul">
          <li>
            We do not sell Apple Health/HealthKit data or Fitbit data.
          </li>
          <li>
            We do not use Apple Health/HealthKit data for advertising,
            marketing, or use-based data mining.
          </li>
          <li>
            We use health and fitness data for the study, research, platform,
            and participant-support purposes described in this Policy and in
            applicable study materials.
          </li>
          <li>
            Revoking device permissions stops future collection through that
            integration, but information already collected may be retained as
            required or permitted by study consent, law, institutional policy, or
            research integrity requirements.
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          You can manage Apple Health permissions in the Apple Health app or iOS
          Settings. Fitbit permissions can be managed through your Fitbit account
          settings or by contacting the study team.
        </Typography>
      </>
    ),
  },
  {
    id: "sharing",
    title: "How we share information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We share information only as needed to provide StudySync, support
          research, comply with obligations, or protect the platform.
        </Typography>
        <Typography component="ul">
          <li>
            <strong>Study teams and research collaborators:</strong> approved
            researchers and collaborators may access study data consistent with
            their study roles, approvals, and permissions.
          </li>
          <li>
            <strong>Service providers:</strong> vendors that help us provide
            authentication, hosting, storage, analytics, communications,
            security, and support services. These providers are allowed to use
            information only to provide services to StudySync or as otherwise
            permitted by law and contract.
          </li>
          <li>
            <strong>Connected services:</strong> Apple, Fitbit, Google, and
            similar services when you authorize an integration or use a related
            feature, or when StudySync must verify, maintain, revoke, or audit
            an account, group, or device connection.
          </li>
          <li>
            <strong>Compliance and safety:</strong> regulators, institutional
            review bodies, legal authorities, or other parties when required by
            law, legal process, institutional policy, or to protect rights,
            safety, and security.
          </li>
          <li>
            <strong>De-identified or aggregated information:</strong> research
            outputs may use data that has been de-identified, aggregated, or
            otherwise prepared in accordance with the applicable study protocol
            and law.
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          StudySync does not sell personal information, connected account data,
          Apple Health/HealthKit data, Fitbit data, or Google account data. We
          do not use that information for advertising or targeted advertising.
        </Typography>
      </>
    ),
  },
  {
    id: "storage-security",
    title: "Storage, security, and protection",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync stores information in secured cloud systems used to operate
          the platform, including application databases, object storage for
          study exports, authentication systems, operational logs, and backup or
          recovery systems where appropriate. StudySync is operated from the
          United States, and service providers may process information in the
          United States or other locations where they operate.
        </Typography>
        <Typography variant="body1" paragraph>
          We use administrative, technical, and organizational safeguards
          designed to protect information, including role-based access controls,
          authentication, encryption in transit, restricted service access,
          logging, and separation of permissions by study role where
          appropriate.
        </Typography>
        <Typography variant="body1" paragraph>
          No electronic system can be guaranteed to be completely secure. You
          should protect your account credentials and contact us promptly if you
          believe your account or study data may have been accessed without
          authorization.
        </Typography>
      </>
    ),
  },
  {
    id: "retention-deletion",
    title: "Retention, deletion, and withdrawal",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We retain information for as long as needed to provide StudySync,
          support research, comply with study protocols, maintain records,
          resolve disputes, meet legal or institutional obligations, and preserve
          research integrity. Specific retention periods may vary by study and
          may be described in the study consent materials.
        </Typography>
        <Typography variant="body1" paragraph>
          You may ask to delete or correct account information, withdraw from a
          study, or stop future connected-device collection. Some deletion
          requests may be limited by study consent, IRB-approved protocols, legal
          obligations, institutional recordkeeping rules, backup retention, or
          research integrity requirements.
        </Typography>
        <Typography component="ul">
          <li>
            To stop future Apple Health, HealthKit, Fitbit, Google, or other
            connected-service access, revoke the permission in that service's
            account settings or contact the study team.
          </li>
          <li>
            To request deletion, correction, access, or study withdrawal, email{" "}
            <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link> or
            contact the study team listed in your study materials. Include the
            email address, study code, participant ID, or tracking ID that helps
            us identify the relevant records.
          </li>
          <li>
            If a request cannot be completed in full, we will explain the reason
            when appropriate and will still stop or limit future collection where
            the study and law permit.
          </li>
        </Typography>
      </>
    ),
  },
  {
    id: "choices-rights",
    title: "Your choices and rights",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Depending on your location, role, and the study you participate in,
          you may have rights to request access, correction, deletion,
          portability, restriction, or objection concerning certain personal
          information. Some rights may be limited by study protocols, legal
          obligations, research integrity requirements, or institutional records
          rules.
        </Typography>
        <Typography component="ul">
          <li>Contact the study team or StudySync support to request access,
            correction, deletion, or withdrawal from a study.</li>
          <li>Use Apple Health, iOS Settings, Fitbit, or other integration
            settings to revoke future device-data access.</li>
          <li>Use account settings or contact us to update researcher account
            information.</li>
          <li>Browser Do Not Track signals are not handled uniformly across the
            industry, and StudySync does not currently respond to them.</li>
        </Typography>
        <Typography variant="body1" paragraph>
          California and other state privacy laws may provide additional rights
          for eligible residents. StudySync does not sell personal information
          and does not use Apple Health/HealthKit data for targeted advertising.
        </Typography>
      </>
    ),
  },
  {
    id: "children",
    title: "Children and minors",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync is intended for users who are at least 18 years old unless a
          specific study permits minors and has obtained any required parent or
          guardian consent and institutional approvals. If you believe a minor
          provided information without required authorization, contact us so we
          can review the situation.
        </Typography>
      </>
    ),
  },
  {
    id: "updates-contact",
    title: "Updates and contact",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We may update this Policy from time to time. The updated version will
          be identified by the "Last updated" date. Material changes may also be
          communicated through StudySync, email, study teams, or other reasonable
          means.
        </Typography>
        <Typography variant="body1" paragraph>
          Questions or requests can be sent to{" "}
          <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>. If you
          are a study participant, you should also review your study consent
          materials for the study team's contact information.
        </Typography>
      </>
    ),
  },
];

const PrivacyPolicy: React.FC = () => {
  return (
    <LegalDocumentLayout
      title="Privacy Policy"
      lastUpdated="May 25, 2026"
      relatedLink={{ label: "Terms of Service", to: "/terms-of-service" }}
      intro={
        <>
          <Typography variant="body1" paragraph>
            StudySync is a research platform for participant enrollment,
            connected device data collection, study dashboards, and research
            data exports.
          </Typography>
          <Typography variant="body1" paragraph>
            This Policy explains what information StudySync collects, why it is
            used, where it is stored, how it may be shared, how it is protected,
            and how participants and researchers can manage privacy requests.
          </Typography>
        </>
      }
      sections={sections}
    />
  );
};

export default PrivacyPolicy;
